// Detailed service catalog — used for /services/:slug detail pages
export const SERVICE_CATALOG = {
  "companion-care": {
    title: "Companion Care",
    icon: "Heart",
    short:
      "Meaningful conversation, shared activities, and a warm presence so no one feels alone at home.",
    metaDescription:
      "Companion Care in Madison, WI. Friendly visits, conversation, hobbies, and emotional support for seniors aging at home. Call 608-513-7639.",
    overview:
      "Loneliness can be as harmful to health as smoking. Our companion caregivers bring warmth, conversation, shared meals, hobbies, and steady presence — turning quiet days into connected ones. We match personalities carefully so the relationship feels natural from day one.",
    benefits: [
      "Reduces isolation and depression",
      "Improves cognitive engagement",
      "Provides a watchful, friendly presence",
      "Encourages activity and routine",
      "Gives family peace of mind",
    ],
    faqs: [
      { q: "How is companion care different from personal care?", a: "Companion care focuses on emotional and social support — conversation, games, walks, meal sharing. Personal care assists with bathing, dressing, hygiene." },
      { q: "Can companions help with light errands?", a: "Yes — grocery runs, mail, prescription pickup, and light housekeeping are all included." },
      { q: "Do caregivers stay consistent?", a: "Always. The same trusted caregiver visits each week, building real relationship and trust." },
    ],
  },
  "personal-care": {
    title: "Personal Care Assistance",
    icon: "Hand",
    short: "Respectful help with bathing, grooming, dressing, and hygiene — always with dignity first.",
    metaDescription:
      "Personal Care Assistance in Madison, WI — bathing, grooming, dressing, and hygiene support delivered with dignity. Call 608-513-7639.",
    overview:
      "Daily personal care is sensitive work. Our caregivers are trained to provide respectful, dignified assistance with bathing, dressing, hair care, oral hygiene, and toileting — preserving privacy, comfort, and independence at every step.",
    benefits: [
      "Reduces fall risk during bathing & transfers",
      "Maintains skin and oral health",
      "Supports independence in daily routines",
      "Trained, gentle, and patient caregivers",
      "Coordinated with medical providers as needed",
    ],
    faqs: [
      { q: "Are caregivers trained in safe transfers?", a: "Yes. All caregivers receive training in safe lifting, transfers, and fall-prevention techniques." },
      { q: "What if my loved one resists bathing?", a: "Our caregivers are trained in gentle redirection and patient communication, especially important for dementia clients." },
    ],
  },
  "dementia-care": {
    title: "Dementia & Memory Support",
    icon: "Brain",
    short: "Patient, specialized care for Alzheimer's and dementia using calming, familiar routines.",
    metaDescription:
      "Specialized Dementia & Alzheimer's Care in Madison, WI — calming routines, redirection, dignity-first communication. Call 608-513-7639.",
    overview:
      "Dementia care is about more than supervision — it's about meeting each person where they are. Our dementia-trained caregivers use validated approaches: familiar routines, gentle redirection, sensory engagement, and calm communication that preserves identity and dignity.",
    benefits: [
      "Reduces agitation and sundowning",
      "Maintains routine and orientation",
      "Engages remaining strengths and joys",
      "Supports family caregivers emotionally",
      "Coordinates with neurology and primary care",
    ],
    faqs: [
      { q: "Do you provide overnight care for wandering?", a: "Yes. Overnight and 24/7 supervision is available for clients with wandering or sundowning behaviors." },
      { q: "Can care begin in early dementia stages?", a: "Absolutely — early engagement helps maintain function longer and builds trust before more support is needed." },
    ],
  },
  "respite-care": {
    title: "Respite Care",
    icon: "Coffee",
    short: "Trusted relief for family caregivers — take a breath while we care for your loved one.",
    metaDescription:
      "Respite Care in Madison, WI — short-term relief for family caregivers. A few hours to a full week. Call 608-513-7639.",
    overview:
      "Family caregivers carry an enormous load. Respite care is your reset — a few hours, a weekend, a week. We step in seamlessly so you can rest, travel, work, or simply breathe without worry.",
    benefits: [
      "Prevents caregiver burnout",
      "Maintains continuity of care",
      "Flexible — hours, overnight, or longer stays",
      "Smooth handoffs with thoughtful communication",
      "Available for vacations, illness, or emergencies",
    ],
    faqs: [
      { q: "How short can respite care be?", a: "As short as a few hours. As long as several weeks. We work around your needs." },
      { q: "Can respite cover holidays?", a: "Yes — we offer holiday, weekend, and overnight coverage." },
    ],
  },
  "medication-reminders": {
    title: "Medication Reminders",
    icon: "Pill",
    short: "Gentle, on-time reminders so prescriptions are never missed or doubled.",
    metaDescription:
      "Medication Reminder Services in Madison, WI — on-time, reliable, gentle reminders for seniors at home. Call 608-513-7639.",
    overview:
      "Missed or doubled medications are a leading cause of hospital readmissions. Our caregivers provide gentle, structured reminders, organize pill boxes, and watch for side effects — coordinating with pharmacy and family whenever needed.",
    benefits: [
      "Prevents missed and doubled doses",
      "Reduces hospital readmissions",
      "Side-effect monitoring",
      "Coordination with pharmacy and family",
      "Documented for clarity and accountability",
    ],
    faqs: [
      { q: "Do caregivers administer medications?", a: "Caregivers remind and observe — medication administration is reserved for licensed medical staff, who we coordinate with when needed." },
    ],
  },
  "transportation": {
    title: "Transportation Assistance",
    icon: "Car",
    short: "Safe rides to appointments, errands, church, and family gatherings around Madison.",
    metaDescription:
      "Senior Transportation Services in Madison, WI — safe rides to doctor visits, errands, church, and family events. Call 608-513-7639.",
    overview:
      "Mobility shouldn't end with driving. Our caregivers provide door-through-door transportation — appointments, errands, religious services, and family events — staying with your loved one through the visit, not just dropping them off.",
    benefits: [
      "Door-through-door support",
      "Trained in safe transfers",
      "Stays with client during appointments",
      "Coordinated scheduling",
      "Maintains community connection",
    ],
    faqs: [
      { q: "Do you provide wheelchair-accessible transport?", a: "We accommodate clients with mobility devices and coordinate with accessible providers when needed." },
    ],
  },
  "meal-preparation": {
    title: "Meal Preparation",
    icon: "Utensils",
    short: "Nutritious, home-cooked meals tailored to dietary needs and personal preferences.",
    metaDescription:
      "Home Meal Preparation for Seniors in Madison, WI — nutritious, dietary-tailored meals cooked at home. Call 608-513-7639.",
    overview:
      "Good food is medicine. Our caregivers plan, shop, and prepare nutritious meals tailored to dietary restrictions, cultural preferences, and personal taste — making mealtimes social, enjoyable, and supportive of long-term health.",
    benefits: [
      "Diabetic, heart-healthy, low-sodium options",
      "Cultural and personal preferences honored",
      "Encourages hydration and balanced nutrition",
      "Shared meals reduce isolation",
      "Pantry organization and grocery support",
    ],
    faqs: [
      { q: "Can caregivers cook to specific diets?", a: "Yes — diabetic, renal, low-sodium, kosher, halal, vegetarian, and culturally specific cuisines." },
    ],
  },
  "light-housekeeping": {
    title: "Light Housekeeping",
    icon: "Home",
    short: "A tidy, calm home — laundry, dishes, dusting, and bed-making done with care.",
    metaDescription:
      "Light Housekeeping for Seniors in Madison, WI — laundry, dishes, dusting, and tidying with respect for your home. Call 608-513-7639.",
    overview:
      "A clean, organized home is a safer home. Our caregivers handle light housekeeping — laundry, dishes, dusting, vacuuming, bed-making, and tidying — quietly and respectfully, treating your home as if it were their own.",
    benefits: [
      "Reduces fall hazards from clutter",
      "Improves air quality and sleep",
      "Supports infection prevention",
      "Frees family caregivers from chores",
      "Maintains dignity of the home environment",
    ],
    faqs: [
      { q: "Is heavy cleaning included?", a: "Light housekeeping focuses on day-to-day tidying. For deep cleaning, we coordinate with trusted partners." },
    ],
  },
  "mobility-assistance": {
    title: "Mobility Assistance",
    icon: "Footprints",
    short: "Steady, attentive support with walking, transfers, and fall prevention at home.",
    metaDescription:
      "Mobility Assistance for Seniors in Madison, WI — safe transfers, fall prevention, ambulation support. Call 608-513-7639.",
    overview:
      "Falls are the leading cause of injury for seniors. Our caregivers are trained in safe transfers, ambulation, and fall prevention — protecting independence while reducing risk through attentive, hands-on support.",
    benefits: [
      "Trained in proper transfer techniques",
      "Walker, cane, and wheelchair support",
      "Home safety assessments included",
      "Encourages safe movement and exercise",
      "Reduces hospital readmission risk",
    ],
    faqs: [
      { q: "Do you provide home safety assessments?", a: "Yes — a home safety walk-through is part of our in-home assessment." },
    ],
  },
  "post-hospital-support": {
    title: "Post-Hospital Support",
    icon: "Stethoscope",
    short: "Compassionate recovery care after a hospital stay to prevent setbacks and promote healing.",
    metaDescription:
      "Post-Hospital Recovery Care in Madison, WI — transition support, fall prevention, medication oversight, healing at home. Call 608-513-7639.",
    overview:
      "The two weeks after discharge are the highest-risk period for setbacks. We coordinate with discharge planners to provide medication oversight, mobility support, meal prep, and watchful eyes — reducing readmission risk and promoting full recovery at home.",
    benefits: [
      "Coordinated with hospital discharge planners",
      "Reduces 30-day readmission risk",
      "Medication and follow-up appointment support",
      "Mobility and nutrition guidance",
      "Available within 24–48 hours of discharge",
    ],
    faqs: [
      { q: "How fast can post-hospital care begin?", a: "We can typically begin within 24–48 hours of discharge — sometimes sooner for urgent transitions." },
    ],
  },
};

export const BLOG_POSTS = [
  {
    slug: "signs-your-parent-needs-home-care",
    title: "Signs Your Parent May Need Home Care",
    category: "Family Caregiving",
    excerpt:
      "Small changes often signal bigger needs. Here's what families in Madison should watch for — and how to gently start the conversation.",
    date: "2025-12-08",
    readTime: "6 min",
    image: "https://images.pexels.com/photos/8088906/pexels-photo-8088906.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    body: [
      "Aging parents rarely announce when they need help. Instead, you notice small things: a missed medication, an unopened pile of mail, a sweater worn inside out, a hesitancy on the stairs.",
      "**1. Weight loss or skipped meals.** Reduced cooking, an empty fridge, or expired food often points to fatigue, depression, or difficulty shopping.",
      "**2. Medication confusion.** Doubled or missed doses are a leading cause of preventable hospitalizations.",
      "**3. Unsteady on their feet.** Look for new bruising, gripping furniture, or hesitation on stairs.",
      "**4. Cognitive shifts.** Repetition, lost names, missed appointments, or new disorientation — especially in familiar settings.",
      "**5. Mail and bills pile up.** Unopened mail or unpaid bills often signal overwhelm or memory changes.",
      "**6. Hygiene changes.** Skipped showers, worn-in clothing, unkempt hair often precede a fall or illness.",
      "**7. Increased isolation.** Pulling away from church, neighbors, or weekly calls is a red flag.",
      "**8. Driving concerns.** New dents, missed turns, getting lost — even briefly.",
      "**9. Home upkeep declines.** Yard, laundry, dishes — caregiving fatigue shows in the small things.",
      "**10. Family caregiver burnout.** If you're depleted, it's time. Respite is help — not failure.",
      "When you see two or more, it's worth a conversation. We at Madison Peaceful Path Home Care offer free in-home assessments and gentle, dignity-first care plans.",
    ],
  },
  {
    slug: "how-to-talk-to-loved-one-about-care",
    title: "How to Talk to a Loved One About Accepting Help",
    category: "Family Caregiving",
    excerpt:
      "The conversation about home care is one of the hardest you'll have. Here's how to approach it with respect, patience, and love.",
    date: "2025-11-20",
    readTime: "7 min",
    image: "https://images.pexels.com/photos/7551596/pexels-photo-7551596.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    body: [
      "Accepting help can feel like losing independence — which is the very thing your loved one is afraid of. The way you start this conversation matters enormously.",
      "**Lead with love, not logistics.** Begin from a place of admiration and concern, not problem-solving.",
      "**Use 'we' language.** \"How can we make this easier?\" lands very differently than \"You need help.\"",
      "**Ask questions; don't deliver verdicts.** \"What would feel supportive right now?\" gives them agency.",
      "**Make it small.** Suggest starting with two hours a week — not a dramatic life change.",
      "**Frame care as a gift to you.** Many parents will accept help to ease their child's worry before they'll accept it for themselves.",
      "**Involve them in choosing.** Let them meet the caregiver. Let them be part of the matching conversation.",
      "**Honor their pace.** This may take more than one conversation. That's okay.",
      "If you're unsure how to start, we offer family consultations specifically designed to help you have this conversation gently and constructively.",
    ],
  },
  {
    slug: "home-care-vs-assisted-living",
    title: "Home Care vs. Assisted Living: Which Is Right for Your Family?",
    category: "Aging in Place",
    excerpt:
      "Both can be excellent choices. Here's an honest comparison of cost, lifestyle, dignity, and quality of life — to help your family decide.",
    date: "2025-11-05",
    readTime: "8 min",
    image: "https://images.pexels.com/photos/7551677/pexels-photo-7551677.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    body: [
      "There's no single right answer — only what's right for your loved one and your family. Here's how to think it through.",
      "**Cost reality.** Assisted living averages $5,000–$7,000+/month in Wisconsin. Home care can range from $1,500 (a few hours/week) to over $20,000 (24/7). Many families find home care more affordable than assumed.",
      "**Independence factor.** Home care preserves familiar routines, neighborhood, and possessions — often delaying or preventing cognitive decline.",
      "**Social life.** Assisted living provides community by design. Home care provides one-on-one connection. Many families combine both — adult day programs plus in-home care.",
      "**Medical needs.** Significant ongoing medical care may point toward a facility. Most non-medical needs can be met beautifully at home.",
      "**Family stress.** Home care reduces caregiver burnout while letting your loved one stay where they're happiest.",
      "**The middle path.** Many families start with home care, evaluate over time, and only consider assisted living when truly necessary.",
      "Want help thinking through your specific situation? Our consultations include an honest discussion of all your options — even ones we don't provide.",
    ],
  },
  {
    slug: "caregiver-burnout-recognize-and-heal",
    title: "Caregiver Burnout: How to Recognize It and What to Do",
    category: "Family Caregiving",
    excerpt:
      "If you're caring for a loved one full-time, exhaustion isn't weakness — it's signal. Here's how to spot burnout early and find real relief.",
    date: "2025-10-22",
    readTime: "6 min",
    image: "https://images.pexels.com/photos/8088906/pexels-photo-8088906.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    body: [
      "Family caregivers are heroes. They're also at high risk for depression, anxiety, sleep disorders, and serious illness. Recognizing burnout early is essential.",
      "**Warning signs.** Constant fatigue. Irritability with your loved one. Withdrawal from friends. Skipping doctor visits or workouts. Crying for no reason. Resenting the person you love.",
      "**Why it matters.** Burned-out caregivers make more mistakes, suffer more health problems, and ultimately can't sustain care. Respite is not selfish — it's strategy.",
      "**What helps.** Schedule rest like it's a medical appointment. Accept help from family. Use respite care for even short breaks. Find a support group (local or online). Talk to your own doctor.",
      "**Respite care, explained.** From a few hours to a full week — a professional caregiver steps in so you can rest, travel, work, or simply breathe.",
      "If you're depleted, please reach out. We've helped many Madison families find rhythm again through structured respite care.",
    ],
  },
  {
    slug: "how-seniors-maintain-independence",
    title: "How Seniors Can Maintain Independence at Home",
    category: "Aging in Place",
    excerpt:
      "Independence isn't about doing everything alone. It's about staying in control of the life you've built. Here's how to support that.",
    date: "2025-10-08",
    readTime: "5 min",
    image: "https://images.pexels.com/photos/7551683/pexels-photo-7551683.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    body: [
      "Independence at home isn't a single thing — it's a constellation of small daily choices. Here's what helps preserve it.",
      "**Make the home safe.** Remove rugs, add grab bars, improve lighting. Falls are the single biggest threat to independence.",
      "**Stay engaged.** Hobbies, conversation, music, faith communities — keep the mind and spirit active.",
      "**Nutrition matters.** Three small balanced meals beat one big one. Hydration is medicine.",
      "**Movement is mandatory.** Even 10 minutes of walking daily preserves balance and mood.",
      "**Accept the right help, early.** Bringing in a few hours of weekly support before a crisis is what allows true independence — not a sign of losing it.",
      "**Plan transportation.** Lost driving doesn't mean lost mobility. Companions who drive change everything.",
      "Our caregivers are trained to support independence — not replace it. We do with, not for.",
    ],
  },
  {
    slug: "madison-senior-resources",
    title: "Madison Senior Resources: Local Support You Should Know About",
    category: "Madison Resources",
    excerpt:
      "A curated guide to senior services, programs, and community resources in and around Madison, Wisconsin.",
    date: "2025-09-30",
    readTime: "7 min",
    image: "https://static.prod-images.emergentagent.com/jobs/d4897236-428b-4abf-b3c0-e7679c5a4973/images/17dacbd358c08b08a71c9b212586df1c0a023f3cb13620d360997562ecb866ab.png",
    body: [
      "Madison is one of the most senior-supportive cities in the Midwest. Here's a starter guide to the resources we recommend most often to our families.",
      "**Aging & Disability Resource Center of Dane County (ADRC).** Free information, options counseling, and benefits navigation for adults 60+ and their families.",
      "**Madison Senior Center (downtown).** Programs, fitness, social activities, and meals for active seniors.",
      "**NewBridge Madison.** Community-based programs, transportation, and case management for adults 55+.",
      "**Veterans services.** Dane County Veterans Service Office connects veterans and surviving spouses to specialized benefits — including some that pay for home care.",
      "**Meal programs.** Meals on Wheels (NewBridge Madison) delivers nutritious meals to seniors throughout the area.",
      "**Faith-based support.** Many Madison churches have lay ministries and senior visiting programs — ask your faith community.",
      "**Hospital discharge planners.** UW Health, SSM Health, UnityPoint–Meriter all have discharge planning teams that can connect families to home care quickly.",
      "Need help navigating any of these? Call us — we're happy to point families to the right place even when we're not the answer.",
    ],
  },
];

export const BLOG_CATEGORIES = [
  "Senior Care Tips",
  "Family Caregiving",
  "Dementia Support",
  "Home Safety",
  "Aging in Place",
  "Madison Resources",
];
